import $ from 'jquery';
import AirportController from '../airport/AirportController';
import EventBus from '../lib/EventBus';
import { EVENT } from '../constants/eventNames';
import { SELECTORS } from '../constants/selectors';
import I18n from '../i18n/I18n';
import { radiansToDegrees } from '../utilities/unitConverters';

const CONTROL_LIMITS = {
    altitude: {
        min: 1000,
        max: 45000,
        step: 1000,
        command: 'a'
    },
    speed: {
        min: 120,
        max: 450,
        step: 10,
        command: 'sp'
    }
};

const QUICK_COMMAND_BUTTONS = [
    {
        action: 'ils',
        i18n: 'quickCommands.ils',
        isVisible: (aircraft) => aircraft.isArrival() && !aircraft.isOnGround()
    },
    {
        action: 'dvs',
        i18n: 'quickCommands.dvs',
        isVisible: (aircraft) => aircraft.isArrival() && !aircraft.isOnGround()
    },
    {
        action: 'taxi',
        i18n: 'quickCommands.taxi',
        isVisible: (aircraft) => aircraft.isDeparture() && aircraft.isOnGround()
    },
    {
        action: 'caf',
        i18n: 'quickCommands.caf',
        isVisible: (aircraft) => aircraft.isDeparture() && aircraft.isOnGround()
    },
    {
        action: 'takeoff',
        i18n: 'quickCommands.takeoff',
        isVisible: (aircraft) => aircraft.isDeparture() && aircraft.isOnGround()
    },
    {
        action: 'cvs',
        i18n: 'quickCommands.cvs',
        isVisible: (aircraft) => aircraft.isDeparture() && !aircraft.isOnGround()
    }
];

const QUICK_COMMANDS_TEMPLATE = '' +
    '<div id="quick-commands" class="quickCommands notSelectable">' +
    '   <span class="quickCommands-callsign"></span>' +
    '   <div class="quickCommands-controls">' +
    '       <label class="quickCommands-control" title="Speed">' +
    '           <span class="quickCommands-controlLabel">SPD <output data-output="speed"></output></span>' +
    '           <input class="quickCommands-slider" data-control="speed" type="range" min="120" max="450" step="10">' +
    '       </label>' +
    '       <label class="quickCommands-control" title="Altitude">' +
    '           <span class="quickCommands-controlLabel">ALT <output data-output="altitude"></output></span>' +
    '           <input class="quickCommands-slider" data-control="altitude" type="range" min="1000" max="45000" step="1000">' +
    '       </label>' +
    '       <div class="quickCommands-headingControl">' +
    '           <button type="button" class="quickCommands-headingDial" title="Drag to select heading" aria-label="Heading">' +
    '               <span class="quickCommands-headingValue">360</span>' +
    '               <span class="quickCommands-assignedHeadingMarker" aria-hidden="true"></span>' +
    '               <span class="quickCommands-targetHeadingMarker" aria-label="Drag to select heading"></span>' +
    '           </button>' +
    '       </div>' +
    '   </div>' +
    '   <div class="quickCommands-buttons"></div>' +
    '</div>';

/**
 * @class QuickCommandPanel
 */
export default class QuickCommandPanel {
    /**
     * @constructor
     * @param $element {JQuery|HTML Element}
     * @param inputController {InputController}
     */
    constructor($element, inputController) {
        this._eventBus = EventBus;
        this._inputController = inputController;
        this.$element = $element;
        this.$panel = null;
        this.$callsign = null;
        this.$buttons = null;
        this.$controls = null;
        this.$headingDial = null;
        this.$headingValue = null;
        this.$assignedHeadingMarker = null;
        this.$targetHeadingMarker = null;
        this._selectedAircraft = null;
        this._buttonElements = {};
        this._headingDialValue = 360;
        this._assignedHeadingValue = 360;
        this._isAdjustingHeading = false;

        this._init()
            ._setupHandlers()
            .enable();
    }

    /**
     * @method _init
     * @chainable
     * @private
     */
    _init() {
        this.$panel = $(QUICK_COMMANDS_TEMPLATE);
        this.$callsign = this.$panel.find('.quickCommands-callsign');
        this.$buttons = this.$panel.find('.quickCommands-buttons');
        this.$controls = this.$panel.find('.quickCommands-controls');
        this.$headingDial = this.$panel.find('.quickCommands-headingDial');
        this.$headingValue = this.$panel.find('.quickCommands-headingValue');
        this.$assignedHeadingMarker = this.$panel.find('.quickCommands-assignedHeadingMarker');
        this.$targetHeadingMarker = this.$panel.find('.quickCommands-targetHeadingMarker');

        QUICK_COMMAND_BUTTONS.forEach((button) => {
            const $button = $(`<button type="button" class="quickCommands-button" data-action="${button.action}"></button>`);

            $button.attr('data-i18n', button.i18n);
            this.$buttons.append($button);
            this._buttonElements[button.action] = $button;
        });

        this.$element.append(this.$panel);
        I18n.applyToDocument(this.$panel);
        this._setPanelVisible(false);

        return this;
    }

    /**
     * @method _setupHandlers
     * @chainable
     * @private
     */
    _setupHandlers() {
        this._onSelectAircraftHandler = this._onSelectAircraft.bind(this);
        this._onDeselectAircraftHandler = this._onDeselectAircraft.bind(this);
        this._onButtonClickHandler = this._onButtonClick.bind(this);
        this._onSliderInputHandler = this._onSliderInput.bind(this);
        this._onSliderChangeHandler = this._onSliderChange.bind(this);
        this._onTargetHeadingMarkerMouseDownHandler = this._onTargetHeadingMarkerMouseDown.bind(this);
        this._onHeadingDialMouseDownHandler = this._onHeadingDialMouseDown.bind(this);
        this._onHeadingDialMouseMoveHandler = this._onHeadingDialMouseMove.bind(this);
        this._onHeadingDialMouseUpHandler = this._onHeadingDialMouseUp.bind(this);

        return this;
    }

    /**
     * @method enable
     * @chainable
     */
    enable() {
        this._eventBus.on(EVENT.SELECT_AIRCRAFT, this._onSelectAircraftHandler);
        this._eventBus.on(EVENT.DESELECT_AIRCRAFT, this._onDeselectAircraftHandler);
        this._eventBus.on(EVENT.LANGUAGE_CHANGE, this._onLanguageChangeHandler = () => {
            I18n.applyToDocument(this.$panel);
        });

        this.$panel.on('click', '.quickCommands-button', this._onButtonClickHandler);
        this.$panel.on('input', '.quickCommands-slider', this._onSliderInputHandler);
        this.$panel.on('change', '.quickCommands-slider', this._onSliderChangeHandler);
        this.$panel.on('mousedown', '.quickCommands-targetHeadingMarker', this._onTargetHeadingMarkerMouseDownHandler);
        this.$panel.on('mousedown', '.quickCommands-headingDial', this._onHeadingDialMouseDownHandler);

        return this;
    }

    /**
     * @method disable
     * @chainable
     */
    disable() {
        this._eventBus.off(EVENT.SELECT_AIRCRAFT, this._onSelectAircraftHandler);
        this._eventBus.off(EVENT.DESELECT_AIRCRAFT, this._onDeselectAircraftHandler);
        this._eventBus.off(EVENT.LANGUAGE_CHANGE, this._onLanguageChangeHandler);
        this.$panel.off('click', '.quickCommands-button', this._onButtonClickHandler);
        this.$panel.off('input', '.quickCommands-slider', this._onSliderInputHandler);
        this.$panel.off('change', '.quickCommands-slider', this._onSliderChangeHandler);
        this.$panel.off('mousedown', '.quickCommands-targetHeadingMarker', this._onTargetHeadingMarkerMouseDownHandler);
        this.$panel.off('mousedown', '.quickCommands-headingDial', this._onHeadingDialMouseDownHandler);
        $(document).off('mousemove', this._onHeadingDialMouseMoveHandler);
        $(document).off('mouseup', this._onHeadingDialMouseUpHandler);

        return this;
    }

    /**
     * @method _onSelectAircraft
     * @param aircraftModel {AircraftModel}
     * @private
     */
    _onSelectAircraft(aircraftModel) {
        this._selectedAircraft = aircraftModel;
        this.$callsign.text(aircraftModel.callsign);
        this._syncControlValues(aircraftModel);
        this._updateButtonVisibility(aircraftModel);
        this._syncWidthWithAirportInfo();
        this._setPanelVisible(true);
    }

    /**
     * @method _onDeselectAircraft
     * @private
     */
    _onDeselectAircraft() {
        this._selectedAircraft = null;
        this.$callsign.text('');
        this._setPanelVisible(false);
    }

    /**
     * @method _updateButtonVisibility
     * @param aircraftModel {AircraftModel}
     * @private
     */
    _updateButtonVisibility(aircraftModel) {
        QUICK_COMMAND_BUTTONS.forEach((button) => {
            const isVisible = button.isVisible(aircraftModel);

            this._buttonElements[button.action].toggleClass(SELECTORS.CLASSNAMES.HIDDEN, !isVisible);
        });
    }

    /**
     * @method _hasVisibleButtons
     * @return {boolean}
     * @private
     */
    _hasVisibleButtons() {
        if (!this._selectedAircraft) {
            return false;
        }

        return QUICK_COMMAND_BUTTONS.some((button) => button.isVisible(this._selectedAircraft));
    }

    /**
     * @method _setPanelVisible
     * @param isVisible {boolean}
     * @private
     */
    _setPanelVisible(isVisible) {
        this.$panel.toggleClass(SELECTORS.CLASSNAMES.OPEN, isVisible);
    }

    /**
     * Keep the control panel aligned with the airport information card.
     *
     * @private
     */
    _syncWidthWithAirportInfo() {
        const airportInfoWidth = this.$element.find('#airportInfo').outerWidth();

        if (airportInfoWidth) {
            this.$panel.css('width', airportInfoWidth);
        }
    }

    /**
     * Update the controls to match the selected aircraft's assigned targets.
     *
     * @param aircraftModel {AircraftModel}
     * @private
     */
    _syncControlValues(aircraftModel) {
        const altitude = this._clampControlValue('altitude', aircraftModel.mcp.altitude);
        const speed = this._clampControlValue('speed', aircraftModel.mcp.speed);
        const heading = this._normalizeHeading(radiansToDegrees(aircraftModel.mcp.heading));

        this.$controls.find('[data-control="altitude"]').val(altitude);
        this.$controls.find('[data-output="altitude"]').text(altitude);
        this.$controls.find('[data-control="speed"]').val(speed);
        this.$controls.find('[data-output="speed"]').text(speed);
        this._setHeadingDialValue(heading);
        this._setAssignedHeadingMarker(heading);
    }

    /**
     * Keep a slider value within the range accepted by its control.
     *
     * @param controlName {string}
     * @param value {number}
     * @return {number}
     * @private
     */
    _clampControlValue(controlName, value) {
        const { min, max, step } = CONTROL_LIMITS[controlName];
        const numericValue = Number(value);
        const clampedValue = Math.min(max, Math.max(min, numericValue));

        return Math.round(clampedValue / step) * step;
    }

    /**
     * Normalize a heading to the command parser's 001-360 range.
     *
     * @param heading {number}
     * @return {number}
     * @private
     */
    _normalizeHeading(heading) {
        const normalizedHeading = ((Math.round(heading) % 360) + 360) % 360;

        return normalizedHeading === 0 ? 360 : normalizedHeading;
    }

    /**
     * Update the visual heading dial without issuing a command.
     *
     * @param heading {number}
     * @private
     */
    _setHeadingDialValue(heading) {
        this._headingDialValue = this._normalizeHeading(heading);
        this.$headingValue.text(String(this._headingDialValue).padStart(3, '0'));
        this.$targetHeadingMarker.css('--target-heading-angle', `${this._headingDialValue}deg`);
    }

    /**
     * Show the heading currently assigned to the aircraft on the dial rim.
     *
     * @param heading {number}
     * @private
     */
    _setAssignedHeadingMarker(heading) {
        this._assignedHeadingValue = this._normalizeHeading(heading);
        this.$assignedHeadingMarker.css('--assigned-heading-angle', `${this._assignedHeadingValue}deg`);
    }

    /**
     * Apply the selected heading and update its assigned-course marker.
     *
     * @private
     */
    _issueHeadingCommand() {
        this._setAssignedHeadingMarker(this._headingDialValue);
        this._inputController.issueQuickCommand(`h ${String(this._headingDialValue).padStart(3, '0')}`);
    }

    /**
     * Display a slider's pending value while it is adjusted.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _onSliderInput(event) {
        const $slider = $(event.currentTarget);
        const controlName = $slider.data('control');

        this.$controls.find(`[data-output="${controlName}"]`).text($slider.val());
    }

    /**
     * Send a slider value through the standard command path.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _onSliderChange(event) {
        if (!this._selectedAircraft) {
            return;
        }

        const $slider = $(event.currentTarget);
        const controlName = $slider.data('control');
        const value = $slider.val();
        const commandValue = controlName === 'altitude' ?
            Math.round(Number(value) / 100) :
            value;

        this._inputController.issueQuickCommand(`${CONTROL_LIMITS[controlName].command} ${commandValue}`);
    }

    /**
     * Begin adjusting the target heading marker on the dial rim.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _onTargetHeadingMarkerMouseDown(event) {
        event.preventDefault();
        event.stopPropagation();
        this._onHeadingDialMouseDown(event);
    }

    /**
     * Begin adjusting the heading dial.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _onHeadingDialMouseDown(event) {
        if (!this._selectedAircraft) {
            return;
        }

        event.preventDefault();
        this._isAdjustingHeading = true;
        this._updateHeadingFromPointer(event);
        $(document).on('mousemove', this._onHeadingDialMouseMoveHandler);
        $(document).one('mouseup', this._onHeadingDialMouseUpHandler);
    }

    /**
     * Update the dial as the pointer moves.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _onHeadingDialMouseMove(event) {
        if (this._isAdjustingHeading) {
            this._updateHeadingFromPointer(event);
        }
    }

    /**
     * Apply the selected heading through the standard command path.
     *
     * @private
     */
    _onHeadingDialMouseUp() {
        if (!this._isAdjustingHeading) {
            return;
        }

        this._isAdjustingHeading = false;
        $(document).off('mousemove', this._onHeadingDialMouseMoveHandler);
        this._issueHeadingCommand();
    }

    /**
     * Calculate a magnetic heading from the pointer's position on the dial.
     *
     * @param event {JQuery.Event}
     * @private
     */
    _updateHeadingFromPointer(event) {
        const dialBounds = this.$headingDial[0].getBoundingClientRect();
        const x = event.clientX - dialBounds.left - (dialBounds.width / 2);
        const y = event.clientY - dialBounds.top - (dialBounds.height / 2);
        const heading = (Math.atan2(x, -y) * 180 / Math.PI + 360) % 360;

        this._setHeadingDialValue(Math.round(heading / 5) * 5);
    }

    /**
     * @method _onButtonClick
     * @param event {JQuery.Event}
     * @private
     */
    _onButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this._selectedAircraft) {
            return;
        }

        const action = $(event.currentTarget).data('action');
        const commandSuffix = this._buildCommandSuffix(action);

        if (!commandSuffix) {
            return;
        }

        this._inputController.issueQuickCommand(commandSuffix);
    }

    /**
     * @method _buildCommandSuffix
     * @param action {string}
     * @return {string|null}
     * @private
     */
    _buildCommandSuffix(action) {
        switch (action) {
            case 'ils':
                return `i ${AirportController.getInitialArrivalRunwayName().toLowerCase()}`;
            case 'dvs':
                return 'dvs';
            case 'taxi':
                return 'taxi';
            case 'caf':
                return 'caf';
            case 'takeoff':
                return 'to';
            case 'cvs':
                return 'cvs';
            default:
                return null;
        }
    }
}
